import React, { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import { Separator } from '../ui/separator';
import {
  Cloud,
  ExternalLink,
  Pencil,
  X,
  Lock,
  Loader2,
  Save,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Plug
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StorageManager() {
  const { isDarkMode } = useTheme();
  
  // Theme-aware classes
  const bgSecondary = isDarkMode ? 'bg-[#1a1a2e]' : 'bg-white';
  const bgTertiary = isDarkMode ? 'bg-[#252540]' : 'bg-gray-100';
  const borderColor = isDarkMode ? 'border-[#2d2d44]' : 'border-gray-200';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-300' : 'text-gray-700';
  const textMuted = isDarkMode ? 'text-gray-400' : 'text-gray-500';

  const [storageSettings, setStorageSettings] = useState({
    idrive_enabled: false,
    idrive_endpoint: '',
    idrive_access_key: '',
    idrive_secret_key: '',
    idrive_bucket: '',
    allow_user_buckets: true
  });
  const [savingStorage, setSavingStorage] = useState(false);
  const [loadingStorage, setLoadingStorage] = useState(true);
  const [isEditingStorage, setIsEditingStorage] = useState(false);
  const [originalStorageSettings, setOriginalStorageSettings] = useState(null);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null); // null, 'success', 'error'
  const [connectionMessage, setConnectionMessage] = useState('');

  useEffect(() => {
    fetchStorageSettings();
  }, []);

  const fetchStorageSettings = async () => {
    setLoadingStorage(true);
    try {
      const response = await axios.get(`${API}/admin/storage-settings`);
      setStorageSettings(response.data);
      setOriginalStorageSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch storage settings:', error);
    } finally {
      setLoadingStorage(false);
    }
  };

  const handleCancelStorageEdit = () => {
    if (originalStorageSettings) {
      setStorageSettings(originalStorageSettings);
    }
    setIsEditingStorage(false);
  };

  const saveStorageSettings = async () => {
    setSavingStorage(true);
    try {
      await axios.put(`${API}/admin/storage-settings`, storageSettings);
      setOriginalStorageSettings(storageSettings);
      setIsEditingStorage(false);
      toast.success('Storage settings saved successfully');
    } catch (error) {
      toast.error('Failed to save storage settings');
      console.error(error);
    } finally {
      setSavingStorage(false);
    }
  };

  const testConnection = async () => {
    // Validate required fields
    if (!storageSettings.idrive_endpoint || !storageSettings.idrive_access_key || 
        !storageSettings.idrive_secret_key || !storageSettings.idrive_bucket) {
      toast.error('Please fill in all storage fields before testing');
      return;
    }

    setTestingConnection(true);
    setConnectionStatus(null);
    setConnectionMessage('');
    
    try {
      const response = await axios.post(`${API}/admin/storage-settings/test`, {
        endpoint: storageSettings.idrive_endpoint,
        access_key: storageSettings.idrive_access_key,
        secret_key: storageSettings.idrive_secret_key,
        bucket: storageSettings.idrive_bucket
      });
      
      setConnectionStatus('success');
      setConnectionMessage(response.data.message || 'Connection successful!');
      toast.success('Connection test successful!');
    } catch (error) {
      setConnectionStatus('error');
      const errorMsg = error.response?.data?.detail || 'Connection failed';
      setConnectionMessage(errorMsg);
      toast.error(`Connection test failed: ${errorMsg}`);
    } finally {
      setTestingConnection(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* iDrive Storage Settings */}
      <Card className={`${bgSecondary} ${borderColor}`}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#6D4AFF]/20 rounded-lg">
                <Cloud className="w-5 h-5 text-[#6D4AFF]" />
              </div>
              <div>
                <CardTitle className={textPrimary}>iDrive E2 Storage</CardTitle>
                <CardDescription className={textMuted}>
                  Configure cloud storage for email attachments
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!isEditingStorage ? (
                <Button
                  onClick={() => setIsEditingStorage(true)}
                  variant="outline"
                  className="border-[#6D4AFF] text-[#6D4AFF] hover:bg-[#6D4AFF] hover:text-white"
                  data-testid="storage-edit-btn"
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              ) : (
                <Button
                  onClick={handleCancelStorageEdit}
                  variant="ghost"
                  className={`${textMuted} hover:${textPrimary}`}
                  data-testid="storage-cancel-btn"
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              )}
              <Button
                onClick={() => window.open('https://console.idrivee2.com/partner/a2gdesigns', '_blank')}
                variant="outline"
                className="border-[#6D4AFF] text-[#6D4AFF] hover:bg-[#6D4AFF] hover:text-white"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Get iDrive Storage
              </Button>
            </div>
          </div>
          {!isEditingStorage && (
            <p className="text-xs text-amber-400 mt-2 flex items-center gap-1">
              <Lock className="w-3 h-3" />
              Settings are locked. Click Edit to make changes.
            </p>
          )}
        </CardHeader>
        <CardContent className="space-y-6">
          {loadingStorage ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 text-[#6D4AFF] animate-spin" />
            </div>
          ) : (
            <>
              {/* Enable iDrive */}
              <div className={`flex items-center justify-between p-4 ${bgTertiary} rounded-lg ${!isEditingStorage ? 'opacity-60' : ''}`}>
                <div>
                  <p className={`${textPrimary} font-medium`}>Enable iDrive Storage</p>
                  <p className={`text-sm ${textMuted}`}>Use iDrive E2 for attachment storage</p>
                </div>
                <Switch
                  checked={storageSettings.idrive_enabled}
                  onCheckedChange={(checked) => setStorageSettings({ ...storageSettings, idrive_enabled: checked })}
                  disabled={!isEditingStorage}
                />
              </div>

              {storageSettings.idrive_enabled && (
                <>
                  <Separator className={isDarkMode ? 'bg-[#2d2d44]' : 'bg-gray-200'} />
                  
                  {/* Credentials */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className={textSecondary}>Endpoint URL</Label>
                      <Input
                        value={storageSettings.idrive_endpoint}
                        onChange={(e) => setStorageSettings({ ...storageSettings, idrive_endpoint: e.target.value })}
                        className={`${bgTertiary} ${borderColor} ${textPrimary} disabled:opacity-60 disabled:cursor-not-allowed`}
                        placeholder="https://e2.idrivee2.com"
                        disabled={!isEditingStorage}
                        data-testid="storage-endpoint-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className={textSecondary}>Bucket Name</Label>
                      <Input
                        value={storageSettings.idrive_bucket}
                        onChange={(e) => setStorageSettings({ ...storageSettings, idrive_bucket: e.target.value })}
                        className={`${bgTertiary} ${borderColor} ${textPrimary} disabled:opacity-60 disabled:cursor-not-allowed`}
                        placeholder="mypatriotmail-storage"
                        disabled={!isEditingStorage}
                        data-testid="storage-bucket-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className={textSecondary}>Access Key</Label>
                      <Input
                        value={storageSettings.idrive_access_key}
                        onChange={(e) => setStorageSettings({ ...storageSettings, idrive_access_key: e.target.value })}
                        className={`${bgTertiary} ${borderColor} ${textPrimary} disabled:opacity-60 disabled:cursor-not-allowed`}
                        placeholder="Your access key"
                        disabled={!isEditingStorage}
                        data-testid="storage-access-key-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className={textSecondary}>Secret Key</Label>
                      <Input
                        type="password"
                        value={storageSettings.idrive_secret_key}
                        onChange={(e) => setStorageSettings({ ...storageSettings, idrive_secret_key: e.target.value })}
                        className={`${bgTertiary} ${borderColor} ${textPrimary} disabled:opacity-60 disabled:cursor-not-allowed`}
                        placeholder="Your secret key"
                        disabled={!isEditingStorage}
                        data-testid="storage-secret-key-input"
                      />
                    </div>
                  </div>

                  {/* Test Connection */}
                  <div className={`p-4 ${bgTertiary} rounded-lg`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className={`${textPrimary} font-medium`}>Test Connection</p>
                        <p className={`text-sm ${textMuted}`}>Verify your iDrive credentials are working</p>
                      </div>
                      <Button
                        onClick={testConnection}
                        disabled={testingConnection || !isEditingStorage}
                        variant="outline"
                        className={`border-[#6D4AFF] text-[#6D4AFF] hover:bg-[#6D4AFF] hover:text-white disabled:opacity-50`}
                        data-testid="storage-test-btn"
                      >
                        {testingConnection ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Testing...
                          </>
                        ) : (
                          <>
                            <Plug className="w-4 h-4 mr-2" />
                            Test Connection
                          </>
                        )}
                      </Button>
                    </div>
                    
                    {/* Connection Status */}
                    {connectionStatus && (
                      <div className={`mt-3 p-3 rounded-lg flex items-center gap-2 ${
                        connectionStatus === 'success' 
                          ? 'bg-green-500/10 border border-green-500/30' 
                          : 'bg-red-500/10 border border-red-500/30'
                      }`}>
                        {connectionStatus === 'success' ? (
                          <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                        ) : (
                          <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                        )}
                        <span className={connectionStatus === 'success' ? 'text-green-400' : 'text-red-400'}>
                          {connectionMessage}
                        </span>
                      </div>
                    )}
                  </div>
                </>
              )}

              <Separator className={isDarkMode ? 'bg-[#2d2d44]' : 'bg-gray-200'} />

              {/* Allow User Buckets */}
              <div className={`flex items-center justify-between p-4 ${bgTertiary} rounded-lg ${!isEditingStorage ? 'opacity-60' : ''}`}>
                <div>
                  <p className={`${textPrimary} font-medium`}>Allow User Custom Buckets</p>
                  <p className={`text-sm ${textMuted}`}>Let users connect their own iDrive buckets for additional storage</p>
                </div>
                <Switch
                  checked={storageSettings.allow_user_buckets}
                  onCheckedChange={(checked) => setStorageSettings({ ...storageSettings, allow_user_buckets: checked })}
                  disabled={!isEditingStorage}
                />
              </div>

              {/* Save Button - Only show when editing */}
              {isEditingStorage && (
                <div className="flex justify-end">
                  <Button
                    onClick={saveStorageSettings}
                    disabled={savingStorage}
                    className="bg-[#6D4AFF] hover:bg-[#5B3FD9]"
                    data-testid="storage-save-btn"
                  >
                    {savingStorage ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Settings
                      </>
                    )}
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className={`${bgSecondary} ${borderColor}`}>
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <AlertCircle className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h4 className={`${textPrimary} font-medium mb-1`}>About iDrive E2 Storage</h4>
              <p className={`text-sm ${textMuted} mb-3`}>
                iDrive E2 provides S3-compatible cloud storage for your email attachments. 
                Users can also connect their own buckets for unlimited storage.
              </p>
              <Button
                onClick={() => window.open('https://console.idrivee2.com/partner/a2gdesigns', '_blank')}
                variant="link"
                className="text-[#6D4AFF] p-0 h-auto"
              >
                Get more storage here <ExternalLink className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
